import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { EditIcon, PlusIcon, TrashIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { collectionDataApi, type CollectionDataItem } from "@/lib/api/collection-data";
import type { ExtensionComponentProps } from "@/extensions/registry";

type DentistSchedule = {
  day: string;
  time: string;
};

type DentistOption = {
  id: number;
  name: string;
  schedules: DentistSchedule[];
};

type UnavailableRow = CollectionDataItem & {
  dentist_id?: number;
  date?: string;
};

type NormalizedUnavailableRow = {
  id: number;
  dentistId: number;
  date: string;
  startMinutes: number;
};

type UnavailableGroup = {
  key: string;
  dentistId: number;
  date: string;
  startMinutes: number;
  endMinutes: number;
  itemIds: number[];
};

const UNAVAILABLE_COLLECTION = "unavailable_schedules";
const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const toTodayIsoDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const minutesToClock = (minutes: number) => {
  const safeValue = Math.max(0, minutes);
  const hours = String(Math.floor(safeValue / 60)).padStart(2, "0");
  const mins = String(safeValue % 60).padStart(2, "0");
  return `${hours}:${mins}`;
};

const parseClockToMinutes = (clock: string) => {
  const [hoursRaw, minutesRaw] = clock.split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }
  return hours * 60 + minutes;
};

const parseTimeRange = (timeRange: string) => {
  const [startClock, endClock] = timeRange.split("-");
  if (!startClock || !endClock) {
    return null;
  }

  const startMinutes = parseClockToMinutes(startClock);
  const endMinutes = parseClockToMinutes(endClock);
  if (startMinutes === null || endMinutes === null || startMinutes >= endMinutes) {
    return null;
  }

  return { startMinutes, endMinutes };
};

const parseSchedules = (value: unknown): DentistSchedule[] => {
  let schedules: unknown = value;

  if (typeof value === "string") {
    try {
      schedules = JSON.parse(value);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(schedules)) {
    return [];
  }

  return schedules
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const day = typeof (entry as Record<string, unknown>).day === "string"
        ? (entry as Record<string, unknown>).day
        : "";
      const time = typeof (entry as Record<string, unknown>).time === "string"
        ? (entry as Record<string, unknown>).time
        : "";

      if (!day || !time) {
        return null;
      }

      return { day, time };
    })
    .filter((item): item is DentistSchedule => Boolean(item));
};

const getAllowedWeekdays = (schedules: DentistSchedule[]) => {
  const set = new Set<string>();
  for (const schedule of schedules) {
    set.add(schedule.day.trim().toLowerCase());
  }
  return set;
};

const findNextAllowedDate = (schedules: DentistSchedule[]) => {
  const allowedWeekdays = getAllowedWeekdays(schedules);
  if (allowedWeekdays.size === 0) {
    return "";
  }

  const base = new Date();
  for (let offset = 0; offset <= 60; offset++) {
    const date = new Date(base.getFullYear(), base.getMonth(), base.getDate() + offset);
    const weekday = WEEKDAY_NAMES[date.getDay()]?.toLowerCase() ?? "";
    if (allowedWeekdays.has(weekday)) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
  }

  return "";
};

const getHourStartsForDate = (dentist: DentistOption | null, dateIso: string) => {
  if (!dentist || !dateIso) {
    return [] as number[];
  }

  const date = new Date(`${dateIso}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return [] as number[];
  }

  const weekday = WEEKDAY_NAMES[date.getDay()]?.toLowerCase() ?? "";
  const candidates: number[] = [];

  for (const schedule of dentist.schedules) {
    if (schedule.day.trim().toLowerCase() !== weekday) {
      continue;
    }

    const parsed = parseTimeRange(schedule.time);
    if (!parsed) {
      continue;
    }

    for (let minute = parsed.startMinutes; minute + 60 <= parsed.endMinutes; minute += 60) {
      candidates.push(minute);
    }
  }

  return Array.from(new Set(candidates)).sort((a, b) => a - b);
};

const parseUnavailableDateTime = (value: unknown) => {
  if (typeof value !== "string") {
    return null;
  }

  const match = value.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}):(\d{2})/);
  if (!match) {
    return null;
  }

  const isoDate = match[1];
  const hours = Number(match[2]);
  const minutes = Number(match[3]);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }

  return {
    date: isoDate,
    startMinutes: hours * 60 + minutes,
  };
};

const buildGroups = (rows: UnavailableRow[]) => {
  const normalizedRows: NormalizedUnavailableRow[] = rows
    .map((row) => {
      const dentistId = Number(row.dentist_id);
      const parsedDate = parseUnavailableDateTime(row.date);
      if (!Number.isInteger(dentistId) || dentistId <= 0 || !parsedDate) {
        return null;
      }

      return {
        id: Number(row.id),
        dentistId,
        date: parsedDate.date,
        startMinutes: parsedDate.startMinutes,
      };
    })
    .filter((item): item is NormalizedUnavailableRow => Boolean(item));

  normalizedRows.sort((a, b) => {
    if (a.dentistId !== b.dentistId) return a.dentistId - b.dentistId;
    const dateSort = a.date.localeCompare(b.date);
    if (dateSort !== 0) return dateSort;
    return a.startMinutes - b.startMinutes;
  });

  const groups: UnavailableGroup[] = [];
  let currentGroup: UnavailableGroup | null = null;

  for (const row of normalizedRows) {
    if (!currentGroup) {
      currentGroup = {
        key: `${row.dentistId}|${row.date}|${row.startMinutes}`,
        dentistId: row.dentistId,
        date: row.date,
        startMinutes: row.startMinutes,
        endMinutes: row.startMinutes + 60,
        itemIds: [row.id],
      };
      continue;
    }

    const isSameDentist = currentGroup.dentistId === row.dentistId;
    const isSameDate = currentGroup.date === row.date;
    const isConsecutive = currentGroup.endMinutes === row.startMinutes;

    if (isSameDentist && isSameDate && isConsecutive) {
      currentGroup.endMinutes += 60;
      currentGroup.itemIds.push(row.id);
      continue;
    }

    groups.push(currentGroup);
    currentGroup = {
      key: `${row.dentistId}|${row.date}|${row.startMinutes}`,
      dentistId: row.dentistId,
      date: row.date,
      startMinutes: row.startMinutes,
      endMinutes: row.startMinutes + 60,
      itemIds: [row.id],
    };
  }

  if (currentGroup) {
    groups.push(currentGroup);
  }

  return groups;
};

const toDateTimeStorageValue = (date: string, minutes: number) => {
  return `${date}T${minutesToClock(minutes)}:00`;
};

export function EscaladeDentalUnavailableSchedules({ tenantSlug, extension }: ExtensionComponentProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const [dentists, setDentists] = useState<DentistOption[]>([]);
  const [rows, setRows] = useState<UnavailableRow[]>([]);

  const [filterDentistId, setFilterDentistId] = useState("all");
  const [filterDate, setFilterDate] = useState("");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingGroupKey, setEditingGroupKey] = useState<string | null>(null);
  const [dentistId, setDentistId] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [dentistItems, unavailableItems] = await Promise.all([
        collectionDataApi.getAll(tenantSlug, "dentists"),
        collectionDataApi.getAll(tenantSlug, UNAVAILABLE_COLLECTION),
      ]);

      const mappedDentists = (dentistItems as CollectionDataItem[])
        .map((item) => {
          const id = Number(item.id);
          if (!Number.isInteger(id) || id <= 0) {
            return null;
          }

          const name = typeof item.name === "string" && item.name.trim()
            ? item.name.trim()
            : `Dentist #${id}`;

          return {
            id,
            name,
            schedules: parseSchedules(item.schedules),
          };
        })
        .filter((item): item is DentistOption => Boolean(item))
        .sort((a, b) => a.name.localeCompare(b.name));

      setDentists(mappedDentists);
      setRows(unavailableItems as UnavailableRow[]);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Failed to load unavailable schedules");
    } finally {
      setIsLoading(false);
    }
  }, [tenantSlug]);

  useEffect(() => {
    void loadData();
  }, [extension.endpoint_path, loadData]);

  const dentistNameMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const item of dentists) {
      map.set(item.id, item.name);
    }
    return map;
  }, [dentists]);

  const groupedRows = useMemo(() => buildGroups(rows), [rows]);

  const filteredGroups = useMemo(() => {
    return groupedRows.filter((group) => {
      if (filterDentistId !== "all" && group.dentistId !== Number(filterDentistId)) {
        return false;
      }

      if (filterDate && group.date !== filterDate) {
        return false;
      }

      return true;
    });
  }, [filterDate, filterDentistId, groupedRows]);

  const selectedDentist = useMemo(() => {
    const parsedId = Number(dentistId);
    if (!Number.isInteger(parsedId) || parsedId <= 0) {
      return null;
    }
    return dentists.find((item) => item.id === parsedId) ?? null;
  }, [dentistId, dentists]);

  const startOptions = useMemo(() => {
    const values = getHourStartsForDate(selectedDentist, date);
    if (editingGroupKey) {
      const editing = groupedRows.find((group) => group.key === editingGroupKey);
      if (editing && !values.includes(editing.startMinutes)) {
        values.push(editing.startMinutes);
        values.sort((a, b) => a - b);
      }
    }
    return values;
  }, [date, editingGroupKey, groupedRows, selectedDentist]);

  const endOptions = useMemo(() => {
    const selectedStart = parseClockToMinutes(startTime);
    if (selectedStart === null) {
      return [] as number[];
    }

    const optionSet = new Set(startOptions);
    const options: number[] = [];

    let cursor = selectedStart + 60;
    while (optionSet.has(cursor)) {
      options.push(cursor);
      cursor += 60;
    }

    options.push(cursor);

    if (editingGroupKey) {
      const editing = groupedRows.find((group) => group.key === editingGroupKey);
      if (editing && editing.endMinutes > selectedStart && !options.includes(editing.endMinutes)) {
        options.push(editing.endMinutes);
      }
    }

    return Array.from(new Set(options)).sort((a, b) => a - b);
  }, [editingGroupKey, groupedRows, startOptions, startTime]);

  useEffect(() => {
    if (editingGroupKey) {
      return;
    }

    if (!selectedDentist) {
      return;
    }

    const weekdaySet = getAllowedWeekdays(selectedDentist.schedules);
    if (weekdaySet.size === 0) {
      setDate("");
      return;
    }

    if (!date) {
      setDate(findNextAllowedDate(selectedDentist.schedules));
      return;
    }

    const selectedDate = new Date(`${date}T00:00:00`);
    if (Number.isNaN(selectedDate.getTime())) {
      setDate(findNextAllowedDate(selectedDentist.schedules));
      return;
    }

    const weekday = WEEKDAY_NAMES[selectedDate.getDay()]?.toLowerCase() ?? "";
    if (!weekdaySet.has(weekday)) {
      setDate(findNextAllowedDate(selectedDentist.schedules));
    }
  }, [date, editingGroupKey, selectedDentist]);

  useEffect(() => {
    if (!startTime) return;
    const startMinutes = parseClockToMinutes(startTime);
    if (startMinutes === null) {
      setStartTime("");
      return;
    }

    const validStart = startOptions.includes(startMinutes);
    if (!validStart) {
      setStartTime("");
      setEndTime("");
      return;
    }

    const endMinutes = parseClockToMinutes(endTime);
    if (endMinutes === null) {
      return;
    }

    const validEnd = endOptions.includes(endMinutes);
    if (!validEnd) {
      setEndTime("");
    }
  }, [endOptions, endTime, startOptions, startTime]);

  const resetForm = () => {
    setEditingGroupKey(null);
    setDentistId("");
    setDate("");
    setStartTime("");
    setEndTime("");
    setIsFormOpen(false);
  };

  const beginCreate = () => {
    setEditingGroupKey(null);
    setDentistId("");
    setDate("");
    setStartTime("");
    setEndTime("");
    setSaveMessage(null);
    setError(null);
    setIsFormOpen(true);
  };

  const beginEdit = (group: UnavailableGroup) => {
    setEditingGroupKey(group.key);
    setDentistId(String(group.dentistId));
    setDate(group.date);
    setStartTime(minutesToClock(group.startMinutes));
    setEndTime(minutesToClock(group.endMinutes));
    setSaveMessage(null);
    setError(null);
    setIsFormOpen(true);
  };

  const handleDeleteGroup = async (group: UnavailableGroup) => {
    setIsSaving(true);
    setError(null);
    setSaveMessage(null);

    try {
      await Promise.all(
        group.itemIds.map((id) => collectionDataApi.delete(tenantSlug, UNAVAILABLE_COLLECTION, id))
      );
      setSaveMessage("Unavailable schedule deleted.");
      await loadData();
      if (editingGroupKey === group.key) {
        resetForm();
      }
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Failed to delete unavailable schedule");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const parsedDentistId = Number(dentistId);
    const startMinutes = parseClockToMinutes(startTime);
    const endMinutes = parseClockToMinutes(endTime);

    if (!Number.isInteger(parsedDentistId) || parsedDentistId <= 0) {
      setError("Please select a dentist.");
      return;
    }

    if (!date) {
      setError("Please select a date.");
      return;
    }

    if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) {
      setError("Please choose a valid time range.");
      return;
    }

    setIsSaving(true);
    setError(null);
    setSaveMessage(null);

    try {
      const rowsToCreate: Array<Record<string, unknown>> = [];
      for (let minute = startMinutes; minute < endMinutes; minute += 60) {
        rowsToCreate.push({
          dentist_id: parsedDentistId,
          date: toDateTimeStorageValue(date, minute),
        });
      }

      if (editingGroupKey) {
        const existingGroup = groupedRows.find((group) => group.key === editingGroupKey);
        if (!existingGroup) {
          setError("The selected unavailable schedule no longer exists.");
          setIsSaving(false);
          return;
        }

        await Promise.all(
          existingGroup.itemIds.map((id) => collectionDataApi.delete(tenantSlug, UNAVAILABLE_COLLECTION, id))
        );
      }

      await Promise.all(
        rowsToCreate.map((payload) => collectionDataApi.create(tenantSlug, UNAVAILABLE_COLLECTION, payload))
      );

      setSaveMessage(editingGroupKey ? "Unavailable schedule updated." : "Unavailable schedule created.");
      resetForm();
      await loadData();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Failed to save unavailable schedule");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="p-4 space-y-4">
      <div className='w-full flex justify-between items-center flex-row'>
        <div>
          <h2 className="text-lg font-semibold">Manage Unavailable Schedules</h2>
          <p className="text-sm text-muted-foreground">
            Block dentist slots by selecting dentist, date, and hourly time range.
          </p>
        </div>
        <div>
          <Button type="button" size="sm" onClick={beginCreate} disabled={isSaving || isLoading}>
            <PlusIcon className="mr-2 size-4" />
            Add Block
          </Button>
        </div>
      </div>

      <div className="flex flex-row items-center gap-2">
        <div className="space-y-1">
          <Label htmlFor="unavailable-filter-dentist">Filter dentist</Label>
          <select
            id="unavailable-filter-dentist"
            value={filterDentistId}
            onChange={(event) => setFilterDentistId(event.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">All dentists</option>
            {dentists.map((dentist) => (
              <option key={dentist.id} value={String(dentist.id)}>
                {dentist.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="unavailable-filter-date">Filter date</Label>
          <input
            id="unavailable-filter-date"
            type="date"
            value={filterDate}
            onChange={(event) => setFilterDate(event.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          />
        </div>

        <div className="flex items-end self-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setFilterDentistId("all");
              setFilterDate("");
            }}
          >
            Reset Filters
          </Button>
        </div>
      </div>

      {error ? <div className="text-sm text-destructive">{error}</div> : null}
      {saveMessage ? <div className="text-sm text-green-700">{saveMessage}</div> : null}
      {isLoading ? <div className="text-sm text-muted-foreground">Loading unavailable schedules...</div> : null}

      {!isLoading ? (
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Time</th>
                <th className="px-3 py-2 font-medium">Dentist</th>
                <th className="px-3 py-2 font-medium">Blocked Slots</th>
                <th className="px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">
                    No unavailable schedules found.
                  </td>
                </tr>
              ) : (
                filteredGroups.map((group) => (
                  <tr key={group.key} className="border-t">
                    <td className="px-3 py-2">{group.date}</td>
                    <td className="px-3 py-2">{minutesToClock(group.startMinutes)}-{minutesToClock(group.endMinutes)}</td>
                    <td className="px-3 py-2">{dentistNameMap.get(group.dentistId) ?? `Dentist #${group.dentistId}`}</td>
                    <td className="px-3 py-2">{group.itemIds.length}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => beginEdit(group)}
                          disabled={isSaving}
                          aria-label={`Edit unavailable schedule on ${group.date}`}
                          title="Edit unavailable schedule"
                        >
                          <EditIcon className="size-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => void handleDeleteGroup(group)}
                          disabled={isSaving}
                          aria-label={`Delete unavailable schedule on ${group.date}`}
                          title="Delete unavailable schedule"
                        >
                          <TrashIcon className="size-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      <Dialog
        open={isFormOpen}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) {
            setEditingGroupKey(null);
            setDentistId("");
            setDate("");
            setStartTime("");
            setEndTime("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGroupKey ? "Update Unavailable Schedule" : "Add Unavailable Schedule"}</DialogTitle>
            <DialogDescription>
              Select dentist, date, and time range to block unavailable booking slots.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="unavailable-dentist">Dentist</Label>
              <select
                id="unavailable-dentist"
                value={dentistId}
                onChange={(event) => {
                  setDentistId(event.target.value);
                  setStartTime("");
                  setEndTime("");
                }}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Select dentist</option>
                {dentists.map((dentist) => (
                  <option key={dentist.id} value={String(dentist.id)}>
                    {dentist.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="unavailable-date">Date</Label>
              <input
                id="unavailable-date"
                type="date"
                value={date}
                min={toTodayIsoDate()}
                onChange={(event) => {
                  setDate(event.target.value);
                  setStartTime("");
                  setEndTime("");
                }}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="unavailable-start">Hour start</Label>
                <select
                  id="unavailable-start"
                  value={startTime}
                  onChange={(event) => {
                    setStartTime(event.target.value);
                    setEndTime("");
                  }}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Start</option>
                  {startOptions.map((minutes) => {
                    const clock = minutesToClock(minutes);
                    return (
                      <option key={clock} value={clock}>
                        {clock}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="unavailable-end">Hour end</Label>
                <select
                  id="unavailable-end"
                  value={endTime}
                  onChange={(event) => setEndTime(event.target.value)}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">End</option>
                  {endOptions.map((minutes) => {
                    const clock = minutesToClock(minutes);
                    return (
                      <option key={clock} value={clock}>
                        {clock}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving || isLoading}>
                {editingGroupKey ? "Update Block" : "Add Block"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
